import { clipSpeed, sourceTimeAt, windowVolumePoints, type VolumePoint } from '@/types/timeline-editing'
/**
 * Playback Resolver - Handles sequence filtering and nested sequence time offset resolution
 *
 * This module solves three key problems:
 * 1. Filtering playback to only show clips from the active sequence
 * 2. Resolving nested sequence time offsets (when a nested clip is at T=5s, its content plays offset by 5s)
 * 3. Flattening nested sequences for audio scheduling
 *
 * Time offset math for nested sequences:
 * - parentPlayhead: Current position in the parent timeline
 * - nestedClip.startOnTimeline: Where the nested clip starts in the parent
 * - nestedClip.trimIn: How much of the source sequence is trimmed from the start
 * - localTime = parentPlayhead - nestedClip.startOnTimeline (position within the nested clip)
 * - sourceTime = nestedClip.trimIn + localTime (position within the source sequence)
 */

import type { Clip, Asset, Track } from '@/types'
import { isAudioAsset, isImageAsset } from '@/lib/assetUtils'
import { resolveVideoPlaybackSource, type VideoPlaybackSource } from '@/lib/videoPlaybackSource'
import { clampAudioFades } from '@/types/shared'

/**
 * Maximum recursion depth for nested sequences.
 * Prevents stack overflow from:
 * - Deeply nested sequence hierarchies (unlikely but possible)
 * - Accidental circular references that bypass creation-time validation
 *
 * 10 levels is generous - typical workflows rarely exceed 2-3 levels of nesting.
 */
const MAX_NESTING_DEPTH = 10

// =============================================================================
// Memoization for flattenSequenceClips
// =============================================================================

/** Cached result of flattenSequenceClips for the current input key */
let cachedFlatClips: FlattenedClip[] | null = null
/** Cache key based on input shape (clips.size-clipIds.size-sequenceId) */
let flatClipsCacheKey: string | null = null
let cachedClipsInput: Map<string, Clip> | null = null
let cachedClipIdsInput: Map<string, Set<string>> | null = null

/**
 * Invalidate the flattenSequenceClips cache.
 * Call when clips are added, removed, moved, or trimmed to ensure fresh data.
 */
export function invalidateFlatClipsCache(): void {
  cachedFlatClips = null
  flatClipsCacheKey = null
  cachedClipsInput = null
  cachedClipIdsInput = null
}

/**
 * Check if a clip references an asset (regular clip).
 * Regular clips have a non-empty assetId and no nestedSequenceId.
 */
function isAssetClip(clip: Clip): boolean {
  return clip.assetId !== '' && !clip.nestedSequenceId
}

/**
 * Check if a clip references a nested sequence.
 * Nested clips have a non-empty nestedSequenceId.
 */
function isNestedSequenceClip(clip: Clip): clip is Clip & { nestedSequenceId: string } {
  return clip.nestedSequenceId !== undefined && clip.nestedSequenceId !== ''
}

/**
 * A resolved clip ready for playback, with computed source time and priority
 */
export interface ResolvedClip {
  /** The clip being displayed */
  clip: Clip
  /** The asset to render */
  asset: Asset
  /** Time within the source asset for frame lookup (in ticks) */
  sourceTimeTicks: number
  /** Start of the resolved clip in the active parent timeline's coordinate space */
  timelineStartTicks: number
  /** End of the resolved clip in the active parent timeline's coordinate space */
  timelineEndTicks: number
  /** Effective track priority (higher wins). For nested clips, this combines parent + child track indices */
  effectiveTrackIndex: number
  /** Interval in the active timeline where this resolution can be reused without crossing a clip boundary. */
  validFromTicks: number
  validUntilTicks: number
}

/**
 * A flattened clip for audio scheduling, with nested sequences expanded.
 * All fields are guaranteed to have valid, non-negative values.
 */
export interface FlattenedClip {
  loop?: boolean
  trimOut?: number
  speed?: number
  preservePitch?: boolean
  clipId?: string
  volumePoints?: VolumePoint[]
  /** The asset ID to play audio from (guaranteed non-empty after flattening) */
  assetId: string
  /** Position in parent timeline (in ticks) - where this clip starts playing */
  effectiveStartTicks: number
  /** Trim in point within the asset (in ticks) - where to start reading the asset */
  trimIn: number
  /** Duration of the clip (in ticks) - guaranteed > 0 */
  duration: number
  /** Track index this clip is on (used for audio/video track filtering) */
  trackIndex: number
  gainDb?: number
  fadeIn?: number
  fadeOut?: number
}

function clampFades(fadeIn: number | undefined, fadeOut: number | undefined, duration: number) {
  const fades = clampAudioFades(fadeIn, fadeOut, duration)
  return {
    fadeIn: fades.fadeIn || undefined,
    fadeOut: fades.fadeOut || undefined,
  }
}

/**
 * Internal context for recursive clip resolution
 */
interface ResolutionContext {
  validFromTicks: number
  validUntilTicks: number
  clips: Map<string, Clip>
  assets: Map<string, Asset>
  clipIdsBySequence: Map<string, Set<string>>
  /** Map of sequence ID to track-index lookup for type and visibility filtering */
  tracksBySequence?: Map<string, Map<number, Track>>
  /** Optional function to resolve an asset ID to its active version (for upscale version groups) */
  getActiveVersionAssetId?: (assetId: string) => string
}

/**
 * Resolve an asset, optionally using version resolution to get the active version.
 * This handles upscale version groups where the clip references the original asset
 * but we want to display/play the currently active version (original or upscaled).
 */
function resolveAsset(ctx: ResolutionContext, assetId: string): Asset | undefined {
  if (ctx.getActiveVersionAssetId) {
    const activeId = ctx.getActiveVersionAssetId(assetId)
    return ctx.assets.get(activeId)
  }
  return ctx.assets.get(assetId)
}

/**
 * Resolve the visible playback clip at a given playhead position.
 * Handles nested sequences by recursively finding clips and applying time offsets.
 *
 * @param clips - All clips in the project
 * @param assets - All assets in the project
 * @param clipIdsBySequence - Index of clip IDs by sequence
 * @param activeSequenceId - The sequence being viewed
 * @param playheadTicks - Current playhead position
 * @param tracks - Optional tracks for type and visibility filtering (audio clips and hidden clips are skipped)
 * @param getActiveVersionAssetId - Optional function to resolve an asset ID to its active version (for upscale version groups)
 * @returns The resolved clip to display, or null if no clip at playhead
 *
 * @example
 * ```ts
 * const resolved = resolvePlaybackClip(clips, assets, clipIdsBySequence, 'seq-1', 48000, tracks);
 * if (resolved) {
 *   const frame = getFrameAtTime(resolved.asset.id, ticksToSeconds(resolved.sourceTimeTicks));
 * }
 * ```
 */
// Per-sequence track lookups keyed by the store's tracks Map identity — the
// yjsStore mints a new Map on any track change, so identity is a correct cache
// key. Resolution runs ~15x/s during playback; rebuilding the lookup each call
// is pure churn.
const tracksBySequenceCache = new WeakMap<Map<string, Track>, Map<string, Map<number, Track>>>()

export function resolvePlaybackClip(
  clips: Map<string, Clip>,
  assets: Map<string, Asset>,
  clipIdsBySequence: Map<string, Set<string>>,
  activeSequenceId: string,
  playheadTicks: number,
  tracks?: Map<string, Track>,
  getActiveVersionAssetId?: (assetId: string) => string,
): ResolvedClip | null {
  // Build per-sequence track lookups for type and visibility filtering.
  // Nested sequences have their own independent track stacks, so recursive
  // resolution must not use the parent sequence's track-index map.
  let tracksBySequence: Map<string, Map<number, Track>> | undefined
  if (tracks) {
    tracksBySequence = tracksBySequenceCache.get(tracks)
    if (!tracksBySequence) {
      const built = new Map<string, Map<number, Track>>()
      tracks.forEach(track => {
        const sequenceTracks = built.get(track.sequenceId) ?? new Map<number, Track>()
        sequenceTracks.set(track.index, track)
        built.set(track.sequenceId, sequenceTracks)
      })
      tracksBySequenceCache.set(tracks, built)
      tracksBySequence = built
    }
  }

  const ctx: ResolutionContext = {
    clips,
    assets,
    clipIdsBySequence,
    tracksBySequence,
    getActiveVersionAssetId,
    validFromTicks: -Infinity,
    validUntilTicks: Infinity,
  }
  const resolved = resolveClipsAtTime(ctx, activeSequenceId, playheadTicks, 0, 0, playheadTicks)
  return resolved ? { ...resolved, validFromTicks: ctx.validFromTicks, validUntilTicks: ctx.validUntilTicks } : null
}

/**
 * Core recursive resolution function. Finds the best clip to display at a given time
 * within a sequence, handling nested sequences with proper time offsets.
 *
 * @param ctx - Resolution context with all data
 * @param sequenceId - Sequence to search within
 * @param timeInSequence - Time position within this sequence (in ticks)
 * @param parentTrackOffset - Accumulated track offset from parent sequences
 * @param depth - Current recursion depth (for overflow protection)
 * @param parentTimelineTicks - Original playhead position in the active parent timeline
 */
function resolveClipsAtTime(
  ctx: ResolutionContext,
  sequenceId: string,
  timeInSequence: number,
  parentTrackOffset: number,
  depth: number,
  parentTimelineTicks: number,
): ResolvedClip | null {
  // Prevent stack overflow from deeply nested or cyclical references
  if (depth >= MAX_NESTING_DEPTH) {
    console.warn(`[playbackResolver] Max nesting depth (${MAX_NESTING_DEPTH}) reached`)
    return null
  }

  const clipIds = ctx.clipIdsBySequence.get(sequenceId)
  if (!clipIds) return null

  let bestCandidate: ResolvedClip | null = null

  for (const clipId of clipIds) {
    const clip = ctx.clips.get(clipId)
    if (!clip) continue

    // Skip clips on audio tracks or hidden tracks
    // Video rendering only processes video tracks
    const tracksByIndex = ctx.tracksBySequence?.get(sequenceId)
    if (tracksByIndex) {
      const track = tracksByIndex.get(clip.trackIndex)
      // Skip audio tracks (track.type === 'audio')
      if (track?.type === 'audio') continue
      // Skip hidden tracks (eye icon toggled off)
      if (track?.isVisible === false) continue
    }

    // Check if time falls within this clip's range
    const clipStart = clip.startOnTimeline
    const clipEnd = clipStart + clip.duration
    // A different track can take over before this clip ends. Include boundaries
    // even for currently inactive clips, and translate nested boundaries into the
    // active timeline. This also handles an upper nested sequence leaving a gap.
    for (const boundary of [clipStart, clipEnd]) {
      const parentBoundary = parentTimelineTicks + boundary - timeInSequence
      if (boundary <= timeInSequence) ctx.validFromTicks = Math.max(ctx.validFromTicks, parentBoundary)
      else ctx.validUntilTicks = Math.min(ctx.validUntilTicks, parentBoundary)
    }
    if (timeInSequence < clipStart || timeInSequence >= clipEnd) continue

    // Calculate time position within this clip
    const localTime = timeInSequence - clipStart

    let candidate: ResolvedClip | null = null

    if (isNestedSequenceClip(clip)) {
      // Nested sequence: recurse with time offset
      // sourceTime = where we are in the source sequence's timeline
      const sourceTime = clip.trimIn + localTime

      // IMPORTANT: Use 0 as the inner offset so clips compete within the nested
      // sequence using their own track indices. The winner inherits this nested
      // clip's effective track level (parentTrackOffset + clip.trackIndex).
      // This ensures a clip on V3 always beats a nested sequence on V2,
      // regardless of the internal track structure of the nested sequence.
      const nestedEffectiveTrack = parentTrackOffset + clip.trackIndex
      const innerResult = resolveClipsAtTime(ctx, clip.nestedSequenceId, sourceTime, 0, depth + 1, parentTimelineTicks)

      if (innerResult) {
        const containerTimelineStart = parentTimelineTicks - localTime
        const containerTimelineEnd = containerTimelineStart + clip.duration
        const timelineStartTicks = Math.max(innerResult.timelineStartTicks, containerTimelineStart)
        const timelineEndTicks = Math.min(innerResult.timelineEndTicks, containerTimelineEnd)

        if (timelineStartTicks < timelineEndTicks) {
          candidate = {
            ...innerResult,
            timelineStartTicks,
            timelineEndTicks,
            effectiveTrackIndex: nestedEffectiveTrack,
          }
        }
      }
    } else if (isAssetClip(clip)) {
      // Regular clip: resolve directly, using version resolution if available
      // This handles upscale version groups where clip references original but we play active version
      const asset = resolveAsset(ctx, clip.assetId)
      // Skip audio assets - they don't have video tracks for rendering
      if (asset && !isAudioAsset(asset)) {
        candidate = {
          clip,
          asset,
          sourceTimeTicks: sourceTimeAt(clip, localTime),
          timelineStartTicks: parentTimelineTicks - localTime,
          timelineEndTicks: parentTimelineTicks - localTime + clip.duration,
          effectiveTrackIndex: parentTrackOffset + clip.trackIndex,
          validFromTicks: parentTimelineTicks - localTime,
          validUntilTicks: parentTimelineTicks - localTime + clip.duration,
        }
      }
    }

    // Keep the candidate with the highest effective track index (V3 wins over V1)
    if (candidate && (!bestCandidate || candidate.effectiveTrackIndex > bestCandidate.effectiveTrackIndex)) {
      bestCandidate = candidate
    }
  }

  return bestCandidate
}

/**
 * Flatten all clips in a sequence, expanding nested sequences into their component clips.
 * Used for audio scheduling where we need a flat list of all audio to play.
 *
 * For nested sequences, clips are expanded with their positions adjusted to reflect
 * where they appear in the parent timeline, respecting trim in/out bounds.
 *
 * @param clips - All clips in the project
 * @param clipIdsBySequence - Index of clip IDs by sequence
 * @param sequenceId - The sequence to flatten
 * @param parentOffset - Position offset from parent timeline (for nested sequences)
 * @param parentTrimIn - Trim start bound from parent (clips before this are excluded)
 * @param parentTrimOut - Trim end bound from parent (clips after this are excluded)
 * @param depth - Current recursion depth (for overflow protection)
 * @returns Array of flattened clips with absolute positions in the timeline
 *
 * @example
 * ```ts
 * const flatClips = flattenSequenceClips(clips, clipIdsBySequence, 'seq-1');
 * for (const clip of flatClips) {
 *   scheduleAudio(clip.assetId, clip.effectiveStartTicks, clip.trimIn, clip.duration);
 * }
 * ```
 */
export function flattenSequenceClips(
  clips: Map<string, Clip>,
  clipIdsBySequence: Map<string, Set<string>>,
  sequenceId: string,
  parentOffset: number = 0,
  parentTrimIn: number = 0,
  parentTrimOut: number = Infinity,
  depth: number = 0,
): FlattenedClip[] {
  // Prevent stack overflow
  if (depth >= MAX_NESTING_DEPTH) {
    console.warn(`[playbackResolver] Max nesting depth (${MAX_NESTING_DEPTH}) reached in flatten`)
    return []
  }

  const clipIds = clipIdsBySequence.get(sequenceId)
  if (!clipIds) return []

  const result: FlattenedClip[] = []

  for (const clipId of clipIds) {
    const clip = clips.get(clipId)
    if (!clip) continue

    // Skip clips entirely outside the parent's trim window
    const clipEnd = clip.startOnTimeline + clip.duration
    if (clipEnd <= parentTrimIn || clip.startOnTimeline >= parentTrimOut) continue

    if (isNestedSequenceClip(clip)) {
      // Nested sequence: recurse with adjusted offset and trim bounds
      //
      // The nested sequence's clips need to be:
      // 1. Offset by where this nested clip appears in the parent
      // 2. Filtered by this nested clip's trim in/out
      //
      // Example: If nested clip starts at T=5s with trimIn=1s:
      // - A clip at T=0s in the source is outside the trim (excluded)
      // - A clip at T=1s in the source appears at T=5s in the parent
      // - A clip at T=2s in the source appears at T=6s in the parent

      // Calculate the offset that maps source sequence time to parent timeline time.
      // If source clip is at T=2s with trimIn=1s and parent clip starts at T=5s:
      // - Source T=1s (first visible frame) maps to parent T=5s
      // - Offset = 5 - 1 = 4, so source T + offset = parent T
      const nestedOffset = parentOffset + clip.startOnTimeline - clip.trimIn

      const nestedClips = flattenSequenceClips(
        clips,
        clipIdsBySequence,
        clip.nestedSequenceId,
        nestedOffset,
        clip.trimIn,
        clip.trimOut,
        depth + 1,
      )

      // Apply parent's trim bounds to the nested results
      // This ensures clips are properly windowed to the parent clip's visible range
      const parentVisibleStart = parentOffset + clip.startOnTimeline
      const parentVisibleEnd = parentVisibleStart + clip.duration

      for (const nestedClip of nestedClips) {
        let adjustedStart = nestedClip.effectiveStartTicks
        let adjustedTrimIn = nestedClip.trimIn
        let adjustedDuration = nestedClip.duration

        // Trim from start if clip starts before parent's visible window
        if (adjustedStart < parentVisibleStart) {
          const trimAmount = parentVisibleStart - adjustedStart
          adjustedStart = parentVisibleStart
          const sourceTrim = trimAmount * clipSpeed(nestedClip)
          adjustedTrimIn += nestedClip.loop ? sourceTrim % (nestedClip.trimOut! - nestedClip.trimIn) : sourceTrim
          adjustedDuration -= trimAmount
        }

        // Trim from end if clip ends after parent's visible window
        const adjustedEnd = adjustedStart + adjustedDuration
        if (adjustedEnd > parentVisibleEnd) {
          adjustedDuration -= adjustedEnd - parentVisibleEnd
        }

        // Only include clips with positive duration after all trimming
        if (adjustedDuration > 0) {
          const fades = clampFades(nestedClip.fadeIn, nestedClip.fadeOut, adjustedDuration)
          result.push({
            assetId: nestedClip.assetId,
            clipId: `${clip.id}/${nestedClip.clipId}`,
            speed: nestedClip.speed,
            preservePitch: nestedClip.preservePitch,
            loop: nestedClip.loop,
            trimOut: nestedClip.loop ? nestedClip.trimOut : undefined,
            volumePoints: windowVolumePoints(
              nestedClip.volumePoints,
              adjustedStart - nestedClip.effectiveStartTicks,
              adjustedDuration,
            ),
            effectiveStartTicks: Math.max(0, adjustedStart),
            trimIn: adjustedTrimIn,
            duration: adjustedDuration,
            trackIndex: nestedClip.trackIndex,
            gainDb: nestedClip.gainDb,
            ...fades,
          })
        }
      }
    } else if (isAssetClip(clip)) {
      // Regular clip: add with offset, applying parent trim bounds
      let effectiveStart = parentOffset + clip.startOnTimeline
      let trimIn = clip.trimIn
      let duration = clip.duration

      // Trim from start if clip starts before parent's trim window
      if (clip.startOnTimeline < parentTrimIn) {
        const trimAmount = parentTrimIn - clip.startOnTimeline
        effectiveStart += trimAmount
        const sourceTrim = trimAmount * clipSpeed(clip)
        trimIn += clip.loop ? sourceTrim % (clip.trimOut - clip.trimIn) : sourceTrim
        duration -= trimAmount
      }

      // Trim from end if clip ends after parent's trim window
      if (clipEnd > parentTrimOut) {
        duration -= clipEnd - parentTrimOut
      }

      // Only include clips with positive duration after all trimming
      if (duration > 0) {
        const fades = clampFades(clip.fadeIn, clip.fadeOut, duration)
        result.push({
          assetId: clip.assetId,
          clipId: clip.id,
          speed: clip.speed,
          preservePitch: clip.preservePitch,
          loop: clip.loop,
          trimOut: clip.loop ? clip.trimOut : undefined,
          volumePoints: windowVolumePoints(
            clip.volumePoints,
            effectiveStart - parentOffset - clip.startOnTimeline,
            duration,
          ),
          effectiveStartTicks: Math.max(0, effectiveStart),
          trimIn,
          duration,
          trackIndex: clip.trackIndex,
          gainDb: clip.gainDb,
          ...fades,
        })
      }
    }
  }

  return result
}

/**
 * Memoized version of flattenSequenceClips.
 * Returns cached result if inputs haven't changed (based on sizes and sequenceId).
 *
 * Note: This uses a simple cache key based on Map sizes, which catches:
 * - Adding/removing clips
 * - Switching sequences
 *
 * For clip edits during playback, use invalidateFlatClipsCache() to force refresh,
 * or the windowed scheduling system will handle updates naturally.
 */
export function flattenSequenceClipsMemoized(
  clips: Map<string, Clip>,
  clipIdsBySequence: Map<string, Set<string>>,
  sequenceId: string,
): FlattenedClip[] {
  const newKey = `${clips.size}-${clipIdsBySequence.size}-${sequenceId}`

  if (
    flatClipsCacheKey === newKey &&
    cachedFlatClips &&
    cachedClipsInput === clips &&
    cachedClipIdsInput === clipIdsBySequence
  ) {
    return cachedFlatClips
  }

  cachedFlatClips = flattenSequenceClips(clips, clipIdsBySequence, sequenceId)
  flatClipsCacheKey = newKey
  cachedClipsInput = clips
  cachedClipIdsInput = clipIdsBySequence
  return cachedFlatClips
}

/**
 * Information about a clip's position relative to the playhead.
 * Used for proximity-based preloading.
 */
export interface ClipProximityInfo {
  /** The asset ID to preload */
  assetId: string
  /** Distance from playhead in ticks (0 = overlaps playhead) */
  distanceFromPlayhead: number
  /** Whether this clip currently overlaps the playhead position */
  overlapsPlayhead: boolean
}

/**
 * Get assets within a preload window around the playhead, sorted by proximity.
 * Only returns clips that are within the specified window - distant clips are excluded.
 *
 * This enables RAM-conscious preloading:
 * - Only loads clips likely to be needed soon
 * - Avoids filling cache with distant clips that cause eviction churn
 * - Clips outside the window load on-demand when user scrubs there
 *
 * @param clips - All clips in the project
 * @param clipIdsBySequence - Index of clip IDs by sequence
 * @param sequenceId - The sequence to check
 * @param playheadTicks - Current playhead position
 * @param windowTicks - Preload window size (clips within ±windowTicks are included)
 * @returns Array of asset IDs within window, sorted by distance (nearest first)
 */
export function getAssetsInPreloadWindow(
  clips: Map<string, Clip>,
  clipIdsBySequence: Map<string, Set<string>>,
  sequenceId: string,
  playheadTicks: number,
  windowTicks: number,
  visited: Set<string> = new Set(),
  depth: number = 0,
): ClipProximityInfo[] {
  // Prevent infinite loops and stack overflow
  if (visited.has(sequenceId) || depth >= MAX_NESTING_DEPTH) {
    return []
  }
  visited.add(sequenceId)

  const clipIds = clipIdsBySequence.get(sequenceId)
  if (!clipIds) return []

  const results: ClipProximityInfo[] = []
  const seenAssetIds = new Set<string>()

  for (const clipId of clipIds) {
    const clip = clips.get(clipId)
    if (!clip) continue

    if (isNestedSequenceClip(clip)) {
      // Check if the nested sequence clip is within window
      const clipStart = clip.startOnTimeline
      const clipEnd = clipStart + clip.duration
      const clipDistance =
        playheadTicks < clipStart ? clipStart - playheadTicks : playheadTicks >= clipEnd ? playheadTicks - clipEnd : 0

      // Skip if entire nested sequence is outside the window
      if (clipDistance > windowTicks) continue

      // Recursively get assets from nested sequence
      const localPlayhead = playheadTicks - clip.startOnTimeline + clip.trimIn
      const nestedResults = getAssetsInPreloadWindow(
        clips,
        clipIdsBySequence,
        clip.nestedSequenceId,
        localPlayhead,
        windowTicks,
        visited,
        depth + 1,
      )
      for (const info of nestedResults) {
        if (!seenAssetIds.has(info.assetId)) {
          seenAssetIds.add(info.assetId)
          results.push(info)
        }
      }
    } else if (isAssetClip(clip)) {
      if (seenAssetIds.has(clip.assetId)) continue

      const clipStart = clip.startOnTimeline
      const clipEnd = clipStart + clip.duration
      const overlapsPlayhead = playheadTicks >= clipStart && playheadTicks < clipEnd

      // Calculate distance to nearest edge
      let distance: number
      if (overlapsPlayhead) {
        distance = 0
      } else if (playheadTicks < clipStart) {
        distance = clipStart - playheadTicks
      } else {
        distance = playheadTicks - clipEnd
      }

      // Only include if within the preload window
      if (distance <= windowTicks) {
        seenAssetIds.add(clip.assetId)
        results.push({
          assetId: clip.assetId,
          distanceFromPlayhead: distance,
          overlapsPlayhead,
        })
      }
    }
  }

  // Sort: overlapping first, then by distance
  results.sort((a, b) => {
    if (a.overlapsPlayhead && !b.overlapsPlayhead) return -1
    if (!a.overlapsPlayhead && b.overlapsPlayhead) return 1
    return a.distanceFromPlayhead - b.distanceFromPlayhead
  })

  return results
}

/**
 * Clip information for pre-warming video sinks.
 */
export interface PrewarmClipInfo {
  videoId: string
  videoUrl: string
  source: VideoPlaybackSource
  startOnTimeline: number
  duration: number
}

/**
 * Get all video clips in a sequence sorted by timeline position.
 * Used for pre-warming video sinks in proximity order.
 *
 * @param clips - All clips in the project
 * @param assets - All assets in the project
 * @param clipIdsBySequence - Index of clip IDs by sequence
 * @param sequenceId - The sequence to get clips from
 * @param getActiveVersionAssetId - Optional function to resolve an asset ID to its active version
 * @returns Array of clips sorted by startOnTimeline, with video URLs resolved
 */
export function getSequenceClipsForPrewarm(
  clips: Map<string, Clip>,
  assets: Map<string, Asset>,
  clipIdsBySequence: Map<string, Set<string>>,
  sequenceId: string,
  getActiveVersionAssetId?: (assetId: string) => string,
): PrewarmClipInfo[] {
  const flattenedClips = flattenSequenceClips(clips, clipIdsBySequence, sequenceId)
  if (flattenedClips.length === 0) return []

  const result: PrewarmClipInfo[] = []

  for (const clip of flattenedClips) {
    // Resolve to active version if version resolution is available
    // This ensures we prewarm the correct proxy for upscaled assets
    const resolvedAssetId = getActiveVersionAssetId ? getActiveVersionAssetId(clip.assetId) : clip.assetId
    const asset = assets.get(resolvedAssetId)
    if (!asset) continue

    // Skip audio assets - they don't have video tracks for pre-warming
    if (isAudioAsset(asset)) continue

    // Skip image assets without a video proxy - they use direct image rendering,
    // not the SinkPool video pipeline (e.g. v9 captures, pre-fix v9 renders)
    if (isImageAsset(asset) && !asset.proxyUri) continue

    const source = resolveVideoPlaybackSource({
      videoId: asset.id,
      proxyUri: asset.proxyUri,
      uri: asset.uri,
    })
    if (!source) continue

    // Include ALL occurrences - same video can appear multiple times on timeline
    // Time-based filtering needs all positions; callers dedupe via isWarm() check
    result.push({
      videoId: source.videoId,
      videoUrl: source.url,
      source,
      startOnTimeline: clip.effectiveStartTicks,
      duration: clip.duration,
    })
  }

  // Sort by timeline position
  result.sort((a, b) => a.startOnTimeline - b.startOnTimeline)

  return result
}

/**
 * Find the index of the clip that contains or is nearest to the playhead position.
 *
 * @param clips - Sorted array of clips from getSequenceClipsForPrewarm
 * @param playheadTicks - Current playhead position
 * @returns Index of the clip at or before playhead, or 0 if before all clips
 */
export function findClipIndexAtPlayhead(clips: PrewarmClipInfo[], playheadTicks: number): number {
  if (clips.length === 0) return 0

  // Find which clip the playhead is in or after
  for (let i = clips.length - 1; i >= 0; i--) {
    if (clips[i].startOnTimeline <= playheadTicks) {
      return i
    }
  }

  return 0
}

/**
 * Get all asset IDs that are used in a sequence (including nested sequences).
 * Useful for preloading media for a sequence without loading the entire project.
 *
 * @param clips - All clips in the project
 * @param clipIdsBySequence - Index of clip IDs by sequence
 * @param sequenceId - The sequence to check
 * @param visited - Set of already visited sequence IDs (for cycle prevention)
 * @param depth - Current recursion depth (for overflow protection)
 * @returns Set of asset IDs used in the sequence
 *
 * @example
 * ```ts
 * const assetIds = getSequenceAssetIds(clips, clipIdsBySequence, activeSequenceId);
 * for (const id of assetIds) {
 *   preloadAsset(id);
 * }
 * ```
 */
export function getSequenceAssetIds(
  clips: Map<string, Clip>,
  clipIdsBySequence: Map<string, Set<string>>,
  sequenceId: string,
  visited: Set<string> = new Set(),
  depth: number = 0,
): Set<string> {
  // Prevent infinite loops and stack overflow
  if (visited.has(sequenceId) || depth >= MAX_NESTING_DEPTH) {
    return new Set()
  }
  visited.add(sequenceId)

  const clipIds = clipIdsBySequence.get(sequenceId)
  if (!clipIds) return new Set()

  const assetIds = new Set<string>()

  for (const clipId of clipIds) {
    const clip = clips.get(clipId)
    if (!clip) continue

    if (isNestedSequenceClip(clip)) {
      // Recursively get assets from nested sequence
      const nestedAssetIds = getSequenceAssetIds(clips, clipIdsBySequence, clip.nestedSequenceId, visited, depth + 1)
      for (const id of nestedAssetIds) {
        assetIds.add(id)
      }
    } else if (isAssetClip(clip)) {
      assetIds.add(clip.assetId)
    }
  }

  return assetIds
}
